import InfiniteScroll from "react-infinite-scroll-component";
import { AccessLevel } from "@/api/williams/users";
import { Contractor } from "@/api/williams/contractors";
import { ExpandableCard, PinButton, useSelectedItems } from "../../components";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFullPath, routes } from "../../routes";
import { getStatusText } from "../lookup/status";
import { highlight } from "@/util";
import { Link } from "react-router-dom";
import { usePermissions } from "../../users/hooks";
import { usePinWelder, useWelderList, useWelderStatus } from "../hooks";
import { useState } from "react";
import { WelderFilters, WelderListView } from "@/api/williams/welders";

export type WelderProps = {
  filters: Partial<WelderFilters>;
  expanded?: boolean;
  allowExpand?: boolean;
  contractor?: Contractor;
};

export function Welders({ filters, contractor, ...props }: WelderProps) {
  const welders = useWelderList(filters);
  const permissions = usePermissions();

  const data = welders.data?.pages.flatMap((page) => page.results) ?? [];
  const total = welders.data?.pages[0]?.total ?? 0;
  const readOnly = permissions.data?.accessLevel === AccessLevel.GeneralUser;

  return (
    <ExpandableCard
      caption={`Welders (${total})`}
      expanded={props.expanded}
      allowExpand={props.allowExpand}
      headerButtons={[
        <Link to={getFullPath(routes.welder.create)} state={{ contractor }} hidden={readOnly}>
          <FontAwesomeIcon icon={faSquarePlus} size="sm" title="Add Welder" />
        </Link>,
      ]}
    >
      <InfiniteScroll
        dataLength={data.length}
        next={welders.fetchNextPage}
        hasMore={Boolean(welders.hasNextPage)}
        loader={<p>Loading...</p>}
      >
        {welders.isLoading ? (
          <p>Loading...</p>
        ) : welders.isError ? (
          <p>An unknown error occurred.</p>
        ) : welders.isSuccess && data.length > 0 ? (
          <WeldersTable data={data} search={filters.searchString} />
        ) : filters.searchString ? (
          <p>Your search returned no results.</p>
        ) : (
          <p>No welders found.</p>
        )}
      </InfiniteScroll>
    </ExpandableCard>
  );
}

export function WeldersTable({ data, search }: { data: WelderListView[]; search?: string }) {
  const allowSelection = useSelectedItems<WelderListView>();

  return (
    <table className="table table-sm table-borderless mb-0">
      <thead>
        <tr>
          {allowSelection && <th className="col-auto" />}
          <th className="col col-md-4">Name</th>
          <th className="col-4 d-none d-md-table-cell">ID Number</th>
          <th className="col-4 d-none d-md-table-cell">Last 4 SSN</th>
          <th className="col-auto" />
        </tr>
      </thead>
      <tbody>
        {data.map((welder) => (
          <WelderRow key={welder.id} welder={welder} search={search} />
        ))}
      </tbody>
    </table>
  );
}

function WelderRow({ welder, search }: { welder: WelderListView; search?: string }) {
  const [expanded, setExpanded] = useState<boolean>(false);

  const pin = usePinWelder();
  const selected = useSelectedItems<WelderListView>();

  function handleSelectWelder(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.checked) {
      selected.add(welder);
    } else {
      selected.remove((x) => x.id === welder.id);
    }
  }

  return (
    <>
      <tr key={`${welder.id}_${welder.pinned}`}>
        {selected && (
          <td className="ps-0" style={{ width: 0 }}>
            <input
              type="checkbox"
              className="form-check-input"
              checked={selected.items.some((x) => x.id === welder.id)}
              onChange={handleSelectWelder}
            />
          </td>
        )}
        <td>
          <Link to={getFullPath(routes.welder.open(welder.id))} className="link link-primary">
            {highlight(welder.name, search)}
          </Link>
        </td>
        <td className="d-none d-md-table-cell">{highlight(welder.badgeNumber, search)}</td>
        <td className="d-none d-md-table-cell">{highlight(welder.ssn, search)}</td>
        <td className="text-end text-nowrap">
          <FontAwesomeIcon
            role="button"
            icon={expanded ? faChevronUp : faChevronDown}
            size="sm"
            className="me-1"
            title="Details"
            onClick={() => setExpanded((prev) => !prev)}
            fixedWidth={true}
          />
          <PinButton pinned={welder.pinned} onChange={() => pin.mutate(welder.id)} />
        </td>
      </tr>
      {expanded && (
        <tr>
          {selected && <td />}
          <td className="d-none d-md-table-cell" colSpan={4}>
            <ExpandedDetails welder={welder} search={search} />
          </td>
          <td className="d-table-cell d-md-none" colSpan={2}>
            <ExpandedDetails welder={welder} search={search} />
          </td>
        </tr>
      )}
    </>
  );
}

function ExpandedDetails({ welder, search }: { welder: WelderListView; search?: string }) {
  const status = useWelderStatus(welder.id);

  return (
    <div className="ms-1">
      <div className="bg-light p-1">Welder Details</div>
      <div className="p-1">
        <dl>
          <>
            <dt>Status</dt>
            <dd>{status.data ? getStatusText(status.data.status) : "Checking"}</dd>
          </>
          {welder.badgeNumber && (
            <>
              <dt className="d-block d-md-none">ID Number</dt>
              <dd className="d-block d-md-none">{highlight(welder.badgeNumber, search)}</dd>
            </>
          )}
          {welder.ssn && (
            <>
              <dt className="d-block d-md-none">Last 4 SSN</dt>
              <dd className="d-block d-md-none">{highlight(welder.ssn, search)}</dd>
            </>
          )}
          {welder.shopField && (
            <>
              <dt>Shop or Field</dt>
              <dd>{welder.shopField}</dd>
            </>
          )}
          {welder.company && (
            <>
              <dt>Company</dt>
              <dd>{welder.company}</dd>
            </>
          )}
          {welder.regions && (
            <>
              <dt>Region(s)</dt>
              <dd>{welder.regions}</dd>
            </>
          )}
        </dl>
      </div>
    </div>
  );
}
